import * as cv from 'opencv.js';

export var invert_y = true;

export
function remove(arr, item) {
  const index = arr.indexOf(item);
  if (index !== -1) {
    arr.splice(index, 1);
    return index;
  }
  return -1;
}
export
function pt_sub(p0, p1) {
  return [p0[0] - p1[0], p0[1] - p1[1]];
}
export
function pt_add(p0, p1) {
  return [p0[0] + p1[0], p0[1] + p1[1]];
}
export
function pt_div(p0, p1) {
  if (p1.length) {
    return [p0[0] / p1[0], p0[1] / p1[1]];
  }

  return [p0[0] / p1, p0[1] / p1];
}
export
function pt_mul(p0, p1) {
  if (p1.length) {
    return [p0[0] * p1[0], p0[1] * p1[1]];
  }

  return [p0[0] * p1, p0[1] * p1];
}
export
function pt_dot(p0, p1) {
  return p0[0] * p1[0] + p0[1] * p1[1];
}
export
function pt_len(p) {
  return Math.sqrt(pt_len_sq(p));
}
export
function pt_len_sq(p) {
  return p[0] * p[0] + p[1] * p[1];
}
export
function pt_distance(p0, p1) {
  return pt_len(pt_sub(p0, p1));
}
/**
 * @returns {[number, number]} centroid of contour
 */
export function get_centroid(contour) {
  let centroid = [0, 0];
  for (const pt of contour) {
    centroid = pt_add(centroid, pt);
  }
  centroid = pt_div(centroid, contour.length);
  return centroid;
}
export
function get_bbox(contour) {
  const bbox = {
    left: Infinity,
    right: -Infinity,
    bottom: Infinity,
    top: -Infinity,
  };
  for (const pt of contour) {
    bbox.left = Math.min(bbox.left, pt[0]);
    bbox.right = Math.max(bbox.right, pt[0]);
    bbox.bottom = Math.min(bbox.bottom, pt[1]);
    bbox.top = Math.max(bbox.top, pt[1]);
  }
  if (invert_y) {
    const tmp = bbox.top;
    bbox.top = bbox.bottom;
    bbox.bottom = tmp;
  }
  return bbox;
}
export
function bbox_to_poly(bbox) {
  if (invert_y) {
    return [
      [bbox.left, bbox.top],
      [bbox.right, bbox.top],
      [bbox.right, bbox.bottom],
      [bbox.left, bbox.bottom],
    ];
  }

  return [
    [bbox.left, bbox.bottom],
    [bbox.right, bbox.bottom],
    [bbox.right, bbox.top],
    [bbox.left, bbox.top],
  ];
}
export
function scale_poly(contour, factor) {
  const centroid = get_centroid(contour);
  const shrunk = [];
  for (const pt of contour) {
    let p = pt_sub(pt, centroid);
    p = pt_mul(p, factor);
    p = pt_add(p, centroid);
    shrunk.push(p);
  }
  return shrunk;
}
export
function line_distance(p0, p1, p2) {
  const l2 = pt_len_sq(pt_sub(p2, p1));
  if (l2 == 0.0) {
    return pt_distance(p0, p1);
  }
  let t = pt_dot(pt_sub(p0, p1), pt_sub(p2, p1)) / l2;
  t = Math.max(0, Math.min(1, t));
  const projection = pt_add(p1, pt_mul(pt_sub(p2, p1), t));
  return pt_distance(p0, projection);
}

export
function simplify_bytes(size) {
  const base = 1000;
  let order = 0;
  while (size / base ** order > base) {
    order += 1;
  }
  const units = [
    'B',
    'kB',
    'MB',
    'GB',
    'TB',
    'PB',
  ];
  return {
    num: size / base ** order,
    units: units[order],
  };
}

// container that supports common selection tasks:
//   - adding elements to selection
//   - removing elements from selection
//   - deleting selection
//   etc
export
class Selection {
  constructor(all = null) {
    this._all = all || [];
    this.selected = [];
  }

  set all(val) {
    this.selected = [];
    this._all = val;
  }

  get all() {
    return this._all;
  }

  // toggles selection for element
  toggle_select(elem) {
    const idx = this.selected.indexOf(elem);
    if (idx != -1) {
      this.selected.splice(idx, 1);
    } else {
      this.selected.push(elem);
    }
  }

  // selects element, or everything if elem === null
  select(elem = null) {
    if (elem !== null) {
      const idx = this.selected.indexOf(elem);
      if (idx == -1) {
        this.selected.push(elem);
      }
    } else {
      this.deselect();
      for (const elem of this._all) {
        this.selected.push(elem);
      }
    }
  }

  // deselects element, or everything if elem === null
  deselect(elem = null) {
    if (elem !== null) {
      remove(this.selected, elem);
    } else {
      this.selected.length = 0;
    }
  }

  // deletes element
  delete(elem) {
    remove(this._all, elem);
    remove(this.selected, elem);
  }

  // deletes selection
  delete_selected() {
    for (const elem of this.selected) {
      remove(this._all, elem);
    }
    this.selected.length = 0;
  }

  // returns true if element is selected, false otherwise
  is_selected(elem) {
    const idx = this.selected.indexOf(elem);
    return idx != -1;
  }
}
// sam utils
/**
 * Convert mat to array [[x,y],[x,y],...]
 * @param cv.mat
 * @returns array
 */
export function matToArray(mat) {
  const array = [];
  for (let j = 0; j < mat.data32S.length; j += 2) {
    array.push([mat.data32S[j], mat.data32S[j + 1]]);
  }
  return array;
}
/**
 * Convert matVector to array of arrays [[[x,y],[x,y],...],[[x,y],[x,y],...],...]
 * @param matVector cv.MatVector
 * @param epsilon float
 * @returns
 */
export function matVectorToArray(matVector, epsilon = null) {
  const array = [];
  for (let i = 0; i < matVector.size(); ++i) {
    const contourMat = matVector.get(i);
    if (epsilon) {
      epsilon = parseFloat(epsilon, 10);
      cv.approxPolyDP(contourMat, contourMat, epsilon, true);
    }
    array.push(matToArray(contourMat));
  }
  return array;
}
/**
 * Find contours in an image
 * @param {Image} img instance of mask Image
 * @param epsilon float point density for approxPolyDP
 * @returns array of arrays [[[x,y],[x,y],...],[[x,y],[x,y],...],...]
 */
export function findSamContours(img, epsilon) {
  const contours = new cv.MatVector();
  const imageMat = cv.imread(img);
  cv.cvtColor(imageMat, imageMat, cv.COLOR_RGB2GRAY, 0);
  cv.findContours(imageMat, contours, new cv.Mat(), cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
  const contoursArray = matVectorToArray(contours, epsilon);
  // Don't forget to release the matrix when you are done
  imageMat.delete();
  contours.delete();
  return contoursArray;
}
/**
 * Check if contour is valid
 * @param {array} contour array of arrays [[x,y],[x,y],...]
 * @returns {boolean}
 */
export function samContourIsValid(contour) {
  if (!contour || contour.length < 3) return false;
  return true;
}
/**
 *  Filter contours , exclude counturs with lees than 3 points, or area less than threshold area
 * @param {array} rawContours array of arrays [[[x,y],[x,y],...],[[x,y],[x,y],...],...]
 * @param {float} threshold float between 0 and 100, filter by contour area
 * @param {Image} img instance of origin Image
 * @returns array of arrays [[[x,y],[x,y],...],[[x,y],[x,y],...],...]
 */
export function filterContours(rawContours, threshold, img) {
  const contours = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const contour of rawContours) {
    if (!samContourIsValid(contour)) continue;
    const mat = cv.matFromArray(contour.length, 1, cv.CV_32SC2, contour.flat());
    const rect = cv.boundingRect(mat);
    const contourArea = (rect.height * rect.width * 100) / (img.width * img.height);
    if (threshold <= contourArea) {
      contours.push(contour);
    }
    mat.delete();
  }
  return contours;
}
export function arrayMove(arr, oldIndex, newIndex) {
  if (typeof arr !== 'object') return;
  if (newIndex < 0 || oldIndex < 0) return;
  if (newIndex >= arr.length || oldIndex >= arr.length) return;
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
}
